/**
 * Time Tracking Service - API calls for Time Tracking module
 * Handles: Time Entries, Time Tracking Codes, Vehicles (parc)
 */

import i18n from '../../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const cm = (key, opts) => i18n.t(`serviceMessages.common.${key}`, opts);

// Helper function to make API calls
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, options);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || cm('http_error', { status: response.status }));
    }
    
    return result;
  } catch (error) {
    console.error(`API Error (${method} ${endpoint}):`, error);
    throw error;
  }
};

// =====================================================
// TIME ENTRIES (Pointage journalier)
// =====================================================

export const getTimeEntries = async () => {
  const result = await apiCall('/time_entries');
  return result.data || [];
};

export const getTimeEntry = async (id) => {
  const result = await apiCall(`/time_entries/${id}`);
  return result.data;
};

export const createTimeEntry = async (entryData) => {
  const now = new Date().toISOString();
  const data = {
    employee_id: entryData.employeeId,
    employee_name: entryData.employeeName,
    date: entryData.date,
    check_in: entryData.checkIn || null,
    check_out: entryData.checkOut || null,
    total_hours: entryData.totalHours || '0:00',
    status: entryData.status || 'P',
    status_label: entryData.statusLabel,
    status_color: entryData.statusColor,
    notes: entryData.notes || '',
    department: entryData.department || null,
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/time_entries', 'POST', data);
  return result.data;
};

export const updateTimeEntry = async (id, entryData) => {
  const data = {
    employee_id: entryData.employeeId,
    employee_name: entryData.employeeName,
    date: entryData.date,
    check_in: entryData.checkIn || null,
    check_out: entryData.checkOut || null,
    total_hours: entryData.totalHours || '0:00',
    status: entryData.status,
    status_label: entryData.statusLabel,
    status_color: entryData.statusColor,
    notes: entryData.notes || '',
    department: entryData.department || null,
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/time_entries/${id}`, 'PUT', data);
  return result.data;
};

export const deleteTimeEntry = async (id) => {
  const result = await apiCall(`/time_entries/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// TIME TRACKING CODES (Codes de pointage)
// =====================================================

export const getTimeTrackingCodes = async () => {
  const result = await apiCall('/time_tracking_codes');
  return result.data || [];
};

export const getTimeTrackingCode = async (id) => {
  const result = await apiCall(`/time_tracking_codes/${id}`);
  return result.data;
};

export const createTimeTrackingCode = async (codeData) => {
  const now = new Date().toISOString();
  const data = {
    code: codeData.code,
    label: codeData.label,
    description: codeData.description || '',
    color: codeData.color,
    bg_color: codeData.bgColor,
    category: codeData.category || 'other',
    is_default: codeData.isDefault ? 1 : 0,
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/time_tracking_codes', 'POST', data);
  return result.data;
};

export const updateTimeTrackingCode = async (id, codeData) => {
  const data = {
    code: codeData.code,
    label: codeData.label,
    description: codeData.description || '',
    color: codeData.color,
    bg_color: codeData.bgColor,
    category: codeData.category || 'other',
    is_default: codeData.isDefault ? 1 : 0,
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/time_tracking_codes/${id}`, 'PUT', data);
  return result.data;
};

export const deleteTimeTrackingCode = async (id) => {
  const result = await apiCall(`/time_tracking_codes/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// VEHICLES (Parc véhicules)
// =====================================================

export const getVehicles = async () => {
  const result = await apiCall('/vehicles');
  return result.data || [];
};

export const getVehicle = async (id) => {
  const result = await apiCall(`/vehicles/${id}`);
  return result.data;
};

export const createVehicle = async (vehicleData) => {
  const now = new Date().toISOString();
  const data = {
    name: vehicleData.name,
    license_plate: vehicleData.licensePlate,
    brand: vehicleData.brand || null,
    model: vehicleData.model || null,
    year: vehicleData.year || null,
    type: vehicleData.type || 'car',
    fuel_type: vehicleData.fuelType || 'diesel',
    status: vehicleData.status || 'available',
    current_driver_id: vehicleData.currentDriverId || null,
    current_driver_name: vehicleData.currentDriverName || null,
    km_counter: vehicleData.kmCounter || 0,
    fuel_level: vehicleData.fuelLevel || 100,
    last_maintenance: vehicleData.lastMaintenance || null,
    next_maintenance: vehicleData.nextMaintenance || null,
    insurance_expiry: vehicleData.insuranceExpiry || null,
    technical_control_expiry: vehicleData.technicalControlExpiry || null,
    notes: vehicleData.notes || '',
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/vehicles', 'POST', data);
  return result.data;
};

export const updateVehicle = async (id, vehicleData) => {
  const data = {
    name: vehicleData.name,
    license_plate: vehicleData.licensePlate,
    brand: vehicleData.brand || null,
    model: vehicleData.model || null,
    year: vehicleData.year || null,
    type: vehicleData.type || 'car',
    fuel_type: vehicleData.fuelType || 'diesel',
    status: vehicleData.status,
    current_driver_id: vehicleData.currentDriverId || null,
    current_driver_name: vehicleData.currentDriverName || null,
    km_counter: vehicleData.kmCounter,
    fuel_level: vehicleData.fuelLevel,
    last_maintenance: vehicleData.lastMaintenance || null,
    next_maintenance: vehicleData.nextMaintenance || null,
    insurance_expiry: vehicleData.insuranceExpiry || null,
    technical_control_expiry: vehicleData.technicalControlExpiry || null,
    notes: vehicleData.notes || '',
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/vehicles/${id}`, 'PUT', data);
  return result.data;
};

export const deleteVehicle = async (id) => {
  const result = await apiCall(`/vehicles/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// TRANSFORM FUNCTIONS (API to Frontend format)
// =====================================================

export const transformTimeEntry = (apiData) => ({
  id: apiData.id,
  employeeId: apiData.employee_id,
  employeeName: apiData.employee_name,
  date: apiData.date,
  checkIn: apiData.check_in,
  checkOut: apiData.check_out,
  totalHours: apiData.total_hours,
  status: apiData.status,
  statusLabel: apiData.status_label,
  statusColor: apiData.status_color,
  notes: apiData.notes,
  department: apiData.department,
  entryRef: apiData.entry_ref,
});

export const transformTimeTrackingCode = (apiData) => {
  const code = apiData.code;
  const inferredType =
    apiData.type ||
    (code != null && String(code).startsWith('V-') ? 'vehicle' : 'employee');
  return {
    id: apiData.id,
    code,
    label: apiData.label,
    description: apiData.description,
    color: apiData.color,
    bgColor: apiData.bg_color,
    category: apiData.category,
    type: inferredType,
    isActive: apiData.is_active !== 0 && apiData.is_active !== false,
    isDefault: apiData.is_default === 1,
  };
};

export const transformVehicle = (apiData) => ({
  id: apiData.id,
  name: apiData.name,
  licensePlate: apiData.license_plate,
  brand: apiData.brand,
  model: apiData.model,
  year: apiData.year,
  type: apiData.type,
  fuelType: apiData.fuel_type,
  status: apiData.status,
  currentDriverId: apiData.current_driver_id,
  currentDriverName: apiData.current_driver_name,
  kmCounter: apiData.km_counter,
  fuelLevel: apiData.fuel_level,
  lastMaintenance: apiData.last_maintenance,
  nextMaintenance: apiData.next_maintenance,
  insuranceExpiry: apiData.insurance_expiry,
  technicalControlExpiry: apiData.technical_control_expiry,
  notes: apiData.notes,
});

export default {
  // Time Entries
  getTimeEntries,
  getTimeEntry,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  transformTimeEntry,
  
  // Time Tracking Codes
  getTimeTrackingCodes,
  getTimeTrackingCode,
  createTimeTrackingCode,
  updateTimeTrackingCode,
  deleteTimeTrackingCode,
  transformTimeTrackingCode,
  
  // Vehicles
  getVehicles,
  getVehicle,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  transformVehicle,
};


